/**
 * 계산 결과의 중간값을 계산서에 실을 "이름 - 값 - 근거" 줄로 바꾼다.
 *
 * 계산서는 **검토자가 표준서를 펴놓고 따라올 수 있어야** 한다.
 * 그래서 값만 적지 않고 어느 식·어느 조항에서 나온 값인지 함께 남긴다.
 * 표시 형식만 다루며 계산은 하지 않는다.
 */
import type { SizingDetail } from "../calc/governingCase";

export interface SheetRow {
  readonly label: string;
  readonly value: string;
  /** 근거 식·조항. 없으면 빈 문자열. */
  readonly source: string;
}

const nf = (v: number, digits = 0) =>
  v.toLocaleString("ko-KR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const eq = (s: string) => s.replace("API 520 Part I ", "식 ");

/** 사이징 결과에서 계산서에 실을 중간값 줄을 만든다. */
export function sizingSheetRows(detail: SizingDetail): SheetRow[] {
  switch (detail.kind) {
    case "gas": {
      const m = detail.result.intermediates;
      const rows: SheetRow[] = [
        {
          label: "유동 영역",
          value:
            detail.result.flowRegime === "critical" ? "임계유동" : "아임계유동",
          source: "5.6.2.5",
        },
        {
          label: "임계압력비 Pcf/P1",
          value: m.criticalPressureRatio.toFixed(4),
          source: "식 (5)",
        },
        {
          label: "임계압력 Pcf",
          value: `${nf(m.criticalFlowPressureKpaAbs, 1)} kPa abs`,
          source: "식 (5)",
        },
        {
          label: "배압비 r = P2/P1",
          value: m.backpressureRatio.toFixed(4),
          source: "5.6.4.1",
        },
      ];
      if (m.coefficientC !== null) {
        rows.push({
          label: "계수 C",
          value: m.coefficientC.toFixed(5),
          source: "식 (13)",
        });
      }
      if (m.coefficientF2 !== null) {
        rows.push({
          label: "아임계 유동계수 F2",
          value: m.coefficientF2.toFixed(4),
          source: "식 (22)",
        });
      }
      rows.push(
        { label: "배출계수 Kd", value: m.kd.toFixed(3), source: "5.6.3.1.1" },
        { label: "배압 보정 Kb", value: m.kb.toFixed(3), source: "5.6.3.1.1" },
        { label: "파열판 조합 Kc", value: m.kc.toFixed(3), source: "5.12.2" },
      );
      return rows;
    }

    case "steam": {
      const m = detail.result.intermediates;
      return [
        {
          label: "Napier 보정 KN",
          value: m.kn.toFixed(4),
          source: eq(m.knEquation),
        },
        {
          label: "과열 보정 KSH",
          value: m.ksh.toFixed(4),
          source:
            m.kshSource === "saturated"
              ? "포화증기 → 1.0 (5.7.1)"
              : m.kshSource === "override"
                ? "직접 지정"
                : `Table 13${m.kshInterpolated ? " (보간)" : ""}`,
        },
        { label: "배출계수 Kd", value: m.kd.toFixed(3), source: "5.7.1" },
        { label: "배압 보정 Kb", value: m.kb.toFixed(3), source: "5.7.1" },
        { label: "파열판 조합 Kc", value: m.kc.toFixed(3), source: "5.12.2" },
      ];
    }

    case "liquid": {
      const m = detail.result.intermediates;
      const rows: SheetRow[] = [
        {
          label: "점도보정 전 면적 AR",
          value: `${nf(m.uncorrectedAreaMm2)} mm²`,
          source: "5.8.1.4",
        },
        {
          label:
            detail.result.equation === "API 520 Part I (43)"
              ? "차압 1.25·Ps − P2"
              : "차압 P1 − P2",
          value: `${nf(m.differentialPressureKpa, 1)} kPa`,
          source: eq(detail.result.equation),
        },
      ];
      if (m.reynolds !== null && m.reynoldsEquation !== null) {
        rows.push(
          {
            label: "레이놀즈수 Re",
            value: nf(m.reynolds),
            source: eq(m.reynoldsEquation),
          },
          {
            label: "Re 계산 기준 오리피스",
            value: m.selectedOrificeForReynolds
              ? `${m.selectedOrificeForReynolds.designation} (${nf(m.selectedOrificeForReynolds.effectiveAreaMm2)} mm²)`
              : "—",
            source: "5.8.1.4",
          },
          {
            label: "점도보정 반복 횟수",
            value: `${m.viscosityIterations}회`,
            source: "5.8.1.5",
          },
        );
      }
      rows.push(
        { label: "점도 보정 Kv", value: m.kv.toFixed(4), source: "식 (34)" },
        { label: "배출계수 Kd", value: m.kd.toFixed(3), source: "5.8.1.3" },
        { label: "배압 보정 Kw", value: m.kw.toFixed(3), source: "5.8.1.3" },
        { label: "파열판 조합 Kc", value: m.kc.toFixed(3), source: "5.12.2" },
      );
      if (m.kp !== null) {
        rows.push({
          label: "과압 보정 Kp",
          value: m.kp.toFixed(3),
          source: "5.9.1 / Figure 39",
        });
      }
      return rows;
    }

    case "two-phase": {
      const m = detail.result.intermediates;
      return [
        {
          label: "유동 영역",
          value:
            detail.result.flowRegime === "critical" ? "임계유동" : "아임계유동",
          source: "식 (C.13)",
        },
        { label: "오메가 ω", value: m.omega.toFixed(4), source: "식 (C.12)" },
        {
          label: "임계압력비 ηc",
          value: m.criticalPressureRatio.toFixed(4),
          source: eq(m.criticalRatioEquation).replace("식 ", "식 "),
        },
        {
          label: "임계압력 Pcf",
          value: `${nf(m.criticalPressureKpaAbs, 1)} kPa abs`,
          source: "식 (C.13)",
        },
        {
          label: "배압비 ηa",
          value: m.backpressureRatio.toFixed(4),
          source: "C.2.2.1",
        },
        {
          label: "질량유속 G",
          value: `${nf(m.massFlux)} kg/s·m²`,
          source: eq(m.massFluxEquation),
        },
        { label: "배출계수 Kd", value: m.kd.toFixed(3), source: "C.2.2.1" },
        { label: "배압 보정 Kb", value: m.kb.toFixed(3), source: "Figure 31" },
        { label: "파열판 조합 Kc", value: m.kc.toFixed(3), source: "5.12.2" },
        { label: "점도 보정 Kv", value: m.kv.toFixed(3), source: "C.2.2.1" },
      ];
    }

    case "subcooled": {
      const m = detail.result.intermediates;
      return [
        {
          label: "과냉 영역",
          value: detail.result.subcoolingRegion === "high" ? "고과냉" : "저과냉",
          source: "식 (C.31)",
        },
        {
          label: "유동 영역",
          value:
            detail.result.flowRegime === "critical" ? "임계유동" : "아임계유동",
          source: "식 (C.33)/(C.34)",
        },
        { label: "오메가 ωs", value: m.omegaS.toFixed(4), source: "식 (C.30)" },
        {
          label: "전이비 ηst",
          value: m.transitionRatio.toFixed(4),
          source: "식 (C.32)",
        },
        {
          label: "포화압력비 ηs",
          value: m.saturationRatio.toFixed(4),
          source: "식 (C.39)",
        },
        {
          label: "임계압력비 ηc",
          value: m.criticalPressureRatio.toFixed(4),
          source: eq(m.criticalRatioSource),
        },
        {
          label: "배압비 ηa",
          value: m.backpressureRatio.toFixed(4),
          source: "식 (C.44)",
        },
        {
          label: "질량유속 G",
          value: `${nf(m.massFlux)} kg/s·m²`,
          source: eq(m.massFluxEquation),
        },
        { label: "배출계수 Kd", value: m.kd.toFixed(3), source: "C.2.3.1" },
        { label: "배압 보정 Kb", value: m.kb.toFixed(3), source: "Figure C.3" },
        { label: "파열판 조합 Kc", value: m.kc.toFixed(3), source: "5.12.2" },
        { label: "점도 보정 Kv", value: m.kv.toFixed(3), source: "C.2.3.1" },
        ...(m.kp !== null
          ? [
              {
                label: "과압 보정 Kp",
                value: m.kp.toFixed(3),
                source: "C.2.3.3",
              },
            ]
          : []),
      ];
    }
  }
}

/** 표 안의 좁은 칸에 쓰는 짧은 유체명. */
export function fluidLabelShort(kind: SizingDetail["kind"]): string {
  switch (kind) {
    case "gas":
      return "기체·증기";
    case "steam":
      return "수증기";
    case "liquid":
      return "액체";
    case "two-phase":
      return "2상";
    case "subcooled":
      return "2상(과냉)";
  }
}

/** 유체 상태 표시명. */
export function fluidLabel(kind: SizingDetail["kind"]): string {
  switch (kind) {
    case "gas":
      return "기체·증기";
    case "steam":
      return "수증기";
    case "liquid":
      return "액체";
    case "two-phase":
      return "2상 유동 (입구 2상)";
    case "subcooled":
      return "2상 유동 (입구 과냉 액체)";
  }
}
